<template>
    <Teleport
        to="#modal-target"
        :disabled="viewMode !== 'overlay'"
    >
        <transition :name="transitionName">
            <div
                v-if="shouldShow"
                :class="[outerClasses, { 'is-minimized': isMinimized }]"
                :style="isMinimized ? {} : inlineStyle"
            >
                <div
                    v-if="!isMinimized"
                    class="ai-chat-resize-handle"
                    @mousedown.prevent="startResize"
                ></div>
                <button
                    class="ai-chat-pull-tab"
                    @click="toggleMinimize"
                >
                    <i :class="['far', 'fa-xs', isMinimized ? 'fa-chevron-left' : 'fa-chevron-right']"></i>
                </button>
                <div
                    v-show="!isMinimized"
                    id="ai-chat-panel"
                    class="ai-chat-sidebar"
                    @click.stop
                >
                    <template v-if="activeConversationId">
                        <ai-chat-header
                            :title="conversationTitle"
                            :show-back="true"
                            :show-new-chat="true"
                            :editable="true"
                            :view-mode="viewMode"
                            @back="backToList"
                            @newChat="newChat"
                            @close="close"
                            @changeMode="changeMode"
                            @rename="renameConversation"
                        />

                        <ai-chat-messages
                            :messages="messages"
                            :conversation-id="activeConversationId"
                            :is-loading="isLoadingMessages"
                            :is-streaming="isStreaming"
                            :is-tool-processing="isToolProcessing"
                            :streaming-content="streamingContent"
                            :current-tool-name="currentToolName"
                            @regenerate="regenerateMessage"
                            @feedback="sendFeedback"
                            @retry="sendMessage"
                        />

                        <!-- Context indicator -->
                        <div
                            v-if="routeContextLabel"
                            class="px-4 pt-2 pb-0 text-xs border-t border-gray-100 flex items-center gap-2"
                        >
                            <button
                                v-tooltip="{ content: includeContext ? 'Click to exclude context' : 'Click to include context', container: '#ai-chat-panel' }"
                                class="flex items-center gap-1"
                                :class="includeContext ? 'text-blue-500' : 'text-muted line-through'"
                                @click="includeContext = !includeContext"
                            >
                                <i :class="['far fa-xs', includeContext ? 'fa-eye' : 'fa-eye-slash']"></i>
                                {{ routeContextLabel }}
                            </button>
                        </div>

                        <ai-chat-input
                            ref="chatInput"
                            :is-streaming="isStreaming"
                            :suggestion="suggestion"
                            @send="sendMessage"
                            @abort="abortStream"
                            @clearSuggestion="() => { suggestion = ''; }"
                        />
                    </template>

                    <template v-else>
                        <ai-chat-header
                            title="Droplet"
                            :show-new-chat="false"
                            :view-mode="viewMode"
                            @close="close"
                            @changeMode="changeMode"
                        />

                        <template v-if="!isLoadingConversations && !conversations.length">
                            <div
                                class="flex-1 flex flex-col items-center justify-center px-6"
                                :class="welcomeTransitioning ? 'ai-chat-welcome-out' : ''"
                            >
                                <div class="ai-chat-welcome-content">
                                    <div class="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center mb-4 mx-auto">
                                        <i class="fas fa-droplet text-blue-500 text-2xl"></i>
                                    </div>
                                    <h2 class="text-lg font-semibold text-gray-800 mb-1 text-center">
                                        How can I help?
                                    </h2>
                                    <p class="text-sm text-muted mb-6 text-center">
                                        Ask me anything about your accounts, orders, or invoices.
                                    </p>
                                </div>
                                <div class="ai-chat-welcome-input w-full max-w-sm">
                                    <ai-chat-input
                                        ref="chatInput"
                                        :is-streaming="isStreaming"
                                        :borderless="true"
                                        @send="sendWelcomeMessage"
                                        @clearSuggestion="() => { suggestion = ''; }"
                                    />
                                </div>
                            </div>
                        </template>

                        <template v-else>
                            <ai-chat-conversation-list
                                :conversations="conversations"
                                :is-loading="isLoadingConversations"
                                @select="openConversation"
                                @delete="deleteConversation"
                            />

                            <ai-chat-input
                                ref="chatInput"
                                :is-streaming="isStreaming"
                                @send="sendMessage"
                                @clearSuggestion="() => { suggestion = ''; }"
                            />
                        </template>
                    </template>
                </div>
            </div>
        </transition>
    </Teleport>
</template>

<script>
import AiChatConversationList from '@components/ai-chat/ai-chat-conversation-list.vue';
import AiChatHeader from '@components/ai-chat/ai-chat-header.vue';
import AiChatInput from '@components/ai-chat/ai-chat-input.vue';
import AiChatMessages from '@components/ai-chat/ai-chat-messages.vue';
import {
    CONTEXT_TYPE_AGING_REPORT,
    CONTEXT_TYPE_AR_MONTH_END,
    CONTEXT_TYPE_AR_RECONCILIATION,
    CONTEXT_TYPE_CUSTOMER,
    CONTEXT_TYPE_INVOICE,
    CONTEXT_TYPE_ORDER,
    CONTEXT_TYPE_PROPOSAL,
    CONTEXT_TYPE_ROUTE,
} from '@constants/ai-chat';

const MIN_WIDTH = 420;
const MAX_WIDTH_PERCENT = 0.5;

export default {
    components: {
        AiChatConversationList,
        AiChatHeader,
        AiChatInput,
        AiChatMessages,
    },

    data() {
        return {
            sidebarWidth: parseInt(localStorage.getItem('ai_chat_sidebar_width'), 10) || MIN_WIDTH,
            isResizing: false,
            welcomeTransitioning: false,
            includeContext: true,
            suggestion: '',
        };
    },

    computed: {
        inlineStyle() {
            return { width: `${this.sidebarWidth}px` };
        },
        isOpen() {
            return this.$store.state.AiChatStore.isOpen;
        },
        isMinimized: {
            get() {
                return this.$store.state.AiChatStore.isMinimized;
            },
            set(value) {
                this.$store.commit('AiChatStore/setIsMinimized', value);
            },
        },
        viewMode() {
            return this.$store.state.AiChatStore.viewMode;
        },
        conversations() {
            return this.$store.state.AiChatStore.conversations;
        },
        activeConversationId() {
            return this.$store.state.AiChatStore.activeConversationId;
        },
        messages() {
            return this.$store.state.AiChatStore.messages;
        },
        isLoadingConversations() {
            return this.$store.state.AiChatStore.isLoadingConversations;
        },
        isLoadingMessages() {
            return this.$store.state.AiChatStore.isLoadingMessages;
        },
        isStreaming() {
            return this.$store.state.AiChatStore.isStreaming;
        },
        isToolProcessing() {
            return this.$store.state.AiChatStore.isToolProcessing;
        },
        streamingContent() {
            return this.$store.state.AiChatStore.streamingContent;
        },
        currentToolName() {
            return this.$store.state.AiChatStore.currentToolName;
        },
        conversationTitle() {
            const conversation = this.$store.getters['AiChatStore/activeConversation'];
            return conversation ? conversation.title : 'New Chat';
        },
        shouldShow() {
            return this.isOpen;
        },
        outerClasses() {
            return this.viewMode === 'sidebar'
                ? 'ai-chat-sidebar-inline'
                : 'ai-chat-overlay';
        },
        transitionName() {
            return this.viewMode === 'sidebar' ? 'ai-chat-push' : 'ai-chat-slide';
        },
        routeContextLabel() {
            const context = this.getRouteContext();
            if (!context) return '';

            return `Viewing ${this.getContextLabel(context)}`;
        },
    },

    watch: {
        shouldShow: {
            immediate: true,
            handler(show) {
                if (show) {
                    this.$store.dispatch('AiChatStore/fetchConversations');

                    if (this.activeConversationId && !this.messages.length) {
                        this.$store.dispatch('AiChatStore/fetchMessages', this.activeConversationId);
                    }

                    this.$nextTick(() => {
                        const input = this.$refs.chatInput;
                        if (input && typeof input.focus === 'function') {
                            try {
                                input.focus();
                            } catch {
                                // Focus may silently fail if input not yet initialized
                            }
                        }
                    });
                }

                this.toggleBodyClass(show);
            },
        },
        viewMode() {
            this.toggleBodyClass(this.shouldShow);
        },
        messages() {
            this.extractSuggestion();
        },
    },

    beforeUnmount() {
        document.body.classList.remove('ai-chat-open');
        document.removeEventListener('mousemove', this.onResize);
        document.removeEventListener('mouseup', this.stopResize);
    },

    methods: {
        close() {
            this.$store.commit('AiChatStore/setOpen', false);
            this.isMinimized = false;
        },

        toggleMinimize() {
            this.isMinimized = !this.isMinimized;
        },

        openConversation(id) {
            this.$store.dispatch('AiChatStore/openConversation', id);
        },

        getRouteContext() {
            const modalContext = this.$store.getters['UiStore/modalContext'];

            // Modal context is the authoritative source for invoice, order, proposal, route
            if (modalContext) {
                return modalContext;
            }

            // Detect context from route params for full page views
            const route = this.$route;

            if (route.params.invoiceId) {
                return {
                    type: CONTEXT_TYPE_INVOICE,
                    invoice_id: route.params.invoiceId,
                    customer_id: route.params.customerId || null,
                };
            }

            if (route.params.orderId) {
                return {
                    type: CONTEXT_TYPE_ORDER,
                    order_id: route.params.orderId,
                    customer_id: route.params.customerId || null,
                };
            }

            if (route.params.proposal_id) {
                return {
                    type: CONTEXT_TYPE_PROPOSAL,
                    proposal_id: route.params.proposal_id,
                };
            }

            if (route.params.route_id) {
                return {
                    type: CONTEXT_TYPE_ROUTE,
                    route_id: route.params.route_id,
                };
            }

            if (route.params.customerId) {
                return {
                    type: CONTEXT_TYPE_CUSTOMER,
                    customer_id: route.params.customerId,
                    customer_location_id: route.params.customerLocationId || null,
                };
            }

            // Detect accounting pages
            if (route.path === '/ar-reconciliation' || route.name === 'ar-reconciliation') {
                return this.getArReconciliationContext();
            }

            if (route.path === '/ar-month-end') {
                return { type: CONTEXT_TYPE_AR_MONTH_END };
            }

            if (route.path === '/aging-reports') {
                return { type: CONTEXT_TYPE_AGING_REPORT };
            }

            return null;
        },

        getArReconciliationContext() {
            const arState = this.$store.state.ArReconciliationStore;

            if (!arState || !arState.startDate) {
                return null;
            }

            return {
                type: CONTEXT_TYPE_AR_RECONCILIATION,
                start_date: arState.startDate,
                end_date: arState.endDate,
                exclude_deposits_ar_month_end: arState.excludeDepositsArMonthEnd,
                exclude_deposits_aging: arState.excludeDepositsAging,
                exclude_overpayments_aging: arState.excludeOverpaymentsAging,
            };
        },

        getContextLabel(context) {
            switch (context.type) {
            case CONTEXT_TYPE_CUSTOMER:
                return `Customer #${context.customer_id}`;
            case CONTEXT_TYPE_INVOICE:
                return `Invoice #${context.invoice_id}`;
            case CONTEXT_TYPE_ORDER:
                return `Order #${context.order_id}`;
            case CONTEXT_TYPE_PROPOSAL:
                return `Proposal #${context.proposal_id}`;
            case CONTEXT_TYPE_ROUTE:
                return `Route #${context.route_id}`;
            case CONTEXT_TYPE_AR_RECONCILIATION:
                return `AR Reconciliation (${context.start_date} to ${context.end_date})`;
            case CONTEXT_TYPE_AR_MONTH_END:
                return 'AR Month End';
            case CONTEXT_TYPE_AGING_REPORT:
                return 'Aging Report';
            default:
                return '';
            }
        },

        async sendMessage(payload) {
            // Handle both string (retry) and object (normal send) formats
            const text = typeof payload === 'string' ? payload : payload.text;
            const images = typeof payload === 'string' ? [] : (payload.images || []);

            this.suggestion = '';
            const context = this.includeContext ? this.getRouteContext() : null;
            await this.$store.dispatch('AiChatStore/sendMessage', { text, context, images });

            if (!this.activeConversationId) {
                await this.$store.dispatch('AiChatStore/fetchConversations');
            }
        },

        extractSuggestion() {
            // Only show suggestions when the last message is from the assistant
            const lastMessage = this.messages[this.messages.length - 1];

            if (!lastMessage || lastMessage.role !== 'assistant' || !lastMessage.content) {
                this.suggestion = '';
                return;
            }

            // Extract SUGGESTION: line from the message
            const lines = lastMessage.content.split('\n');
            const suggestionLine = lines.find((line) => line.startsWith('SUGGESTION:'));

            if (suggestionLine) {
                this.suggestion = suggestionLine.replace(/^SUGGESTION:\s*/, '').trim();
            } else {
                this.suggestion = '';
            }
        },

        sendFeedback({ messageId, feedback }) {
            this.$store.dispatch('AiChatStore/sendFeedback', { messageId, feedback });
        },

        regenerateMessage() {
            this.$store.dispatch('AiChatStore/regenerateLastMessage');
        },

        renameConversation(title) {
            this.$store.dispatch('AiChatStore/renameConversation', {
                id: this.activeConversationId,
                title,
            });
        },

        async sendWelcomeMessage(text) {
            this.welcomeTransitioning = true;

            setTimeout(() => {
                this.sendMessage(text);
            }, 400);
        },

        newChat() {
            this.$store.dispatch('AiChatStore/startNewConversation');
        },

        backToList() {
            this.$store.dispatch('AiChatStore/startNewConversation');
        },

        deleteConversation(id) {
            this.$store.dispatch('AiChatStore/deleteConversation', id);
        },

        abortStream() {
            this.$store.dispatch('AiChatStore/abortStream');
        },

        changeMode(mode) {
            this.$store.commit('AiChatStore/setViewMode', mode);
        },

        toggleBodyClass(isVisible) {
            if (isVisible) {
                document.body.classList.add('ai-chat-open');
            } else {
                document.body.classList.remove('ai-chat-open');
            }
        },

        startResize() {
            this.isResizing = true;
            document.addEventListener('mousemove', this.onResize);
            document.addEventListener('mouseup', this.stopResize);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';
        },

        onResize(e) {
            const maxWidth = Math.floor(window.innerWidth * MAX_WIDTH_PERCENT);
            const width = window.innerWidth - e.clientX;

            this.sidebarWidth = Math.max(MIN_WIDTH, Math.min(width, maxWidth));
        },

        stopResize() {
            this.isResizing = false;
            document.removeEventListener('mousemove', this.onResize);
            document.removeEventListener('mouseup', this.stopResize);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            localStorage.setItem('ai_chat_sidebar_width', this.sidebarWidth);
        },
    },
};
</script>

<style scoped>
    /* Overlay mode styles */
    .ai-chat-overlay {
        position: fixed;
        top: 0;
        right: 0;
        bottom: 0;
        max-width: 100vw;
        z-index: 100001;
        pointer-events: none;
    }

    /* Sidebar (push) mode styles */
    .ai-chat-sidebar-inline {
        width: 420px;
        height: 100%;
        flex-shrink: 0;
        position: relative;
    }

    .ai-chat-resize-handle {
        position: absolute;
        top: 0;
        left: -3px;
        width: 6px;
        height: 100%;
        cursor: col-resize;
        z-index: 10;
        pointer-events: auto;
    }

    .ai-chat-resize-handle:hover {
        background: rgba(59, 130, 246, 0.3);
    }

    .ai-chat-pull-tab {
        position: absolute;
        top: 50%;
        left: -14px;
        transform: translateY(-50%);
        width: 14px;
        height: 48px;
        background: #f3f4f6;
        border: 1px solid #e5e7eb;
        border-right: none;
        border-radius: 6px 0 0 6px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        color: #374151;
        z-index: 11;
        pointer-events: auto;
        transition: color 0.15s, background 0.15s;
    }

    .ai-chat-pull-tab:hover {
        color: #3b82f6;
        background: #eff6ff;
    }

    .is-minimized {
        width: 0 !important;
    }

    .is-minimized .ai-chat-pull-tab {
        left: -14px;
    }

    .ai-chat-sidebar {
        position: relative;
        width: 100%;
        height: 100%;
        background: white;
        border-left: 1px solid #e5e7eb;
        display: flex;
        flex-direction: column;
        pointer-events: auto;
    }

    /* Overlay slide transition */
    .ai-chat-slide-enter-active,
    .ai-chat-slide-leave-active {
        transition: opacity 0.2s ease;
    }

    .ai-chat-slide-enter-active .ai-chat-sidebar,
    .ai-chat-slide-leave-active .ai-chat-sidebar {
        transition: transform 0.25s ease;
    }

    .ai-chat-slide-enter,
    .ai-chat-slide-leave-to {
        opacity: 0;
    }

    .ai-chat-slide-enter .ai-chat-sidebar,
    .ai-chat-slide-leave-to .ai-chat-sidebar {
        transform: translateX(100%);
    }

    /* Welcome screen transition */
    .ai-chat-welcome-content {
        transition: opacity 0.35s ease, transform 0.35s ease;
    }

    .ai-chat-welcome-input {
        transition: transform 0.4s ease;
    }

    .ai-chat-welcome-out .ai-chat-welcome-content {
        opacity: 0;
        transform: translateY(-30px);
    }

    .ai-chat-welcome-out .ai-chat-welcome-input {
        transform: translateY(40vh);
    }

    /* Sidebar push transition */
    .ai-chat-push-enter-active,
    .ai-chat-push-leave-active {
        transition: width 0.25s ease, opacity 0.2s ease;
        overflow: hidden;
    }

    .ai-chat-push-enter,
    .ai-chat-push-leave-to {
        width: 0 !important;
        opacity: 0;
    }
</style>
